import { diffWordsWithSpace } from "diff";
const rowKey = ({ i: _, ...row })=>JSON.stringify(row);
/** What changed from the snapshot the server holds as `base`: every uid in order, and full rows only where they differ. */ export function snapshotDelta(base, next) {
    const known = new Map(base.paragraphs.map((paragraph)=>[
            paragraph.uid,
            rowKey(paragraph)
        ]));
    const isSame = (before, after)=>JSON.stringify(before) === JSON.stringify(after);
    return {
        version: next.version,
        base: base.version,
        order: next.paragraphs.map(({ uid, hash })=>({
                uid,
                hash
            })),
        paragraphs: next.paragraphs.filter((paragraph)=>known.get(paragraph.uid) !== rowKey(paragraph)),
        selection: next.selection,
        ...isSame(base.changes, next.changes) ? {} : {
            changes: next.changes
        },
        ...isSame(base.comments, next.comments) ? {} : {
            comments: next.comments
        }
    };
}
export const MAX_SNAPSHOT_BYTES = 2 * 1024 * 1024;
export function wordHash(text) {
    let hash = 0x811c9dc5;
    for(let index = 0; index < text.length; index++){
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, "0");
}
export const changeHash = (change)=>wordHash(`${change.type}\n${change.author}\n${change.date}\n${change.text}`);
const byteLength = (value)=>new TextEncoder().encode(JSON.stringify(value)).length;
const cutTo = (paragraphs, cap)=>paragraphs.map((paragraph)=>paragraph.text.length > cap ? {
            ...paragraph,
            text: paragraph.text.slice(0, cap),
            cut: true
        } : paragraph);
// ponytail: binary search re-serialises the snapshot each step (~17 passes), only on documents over 2 MB
export function fitSnapshot(snapshot, maxBytes = MAX_SNAPSHOT_BYTES) {
    if (byteLength(snapshot) <= maxBytes) return snapshot;
    let low = 0;
    let high = Math.max(...snapshot.paragraphs.map((paragraph)=>paragraph.text.length));
    while(low < high){
        const cap = Math.ceil((low + high) / 2);
        if (byteLength({
            ...snapshot,
            paragraphs: cutTo(snapshot.paragraphs, cap)
        }) <= maxBytes) low = cap;
        else high = cap - 1;
    }
    return {
        ...snapshot,
        paragraphs: cutTo(snapshot.paragraphs, low)
    };
}
const MAX_REMOVED_SHARE = 0.6;
const MAX_QUERY_LENGTH = 255;
export const wordQuery = (text)=>text.replace(/\^/g, "^^").replace(/\t/g, "^t");
function startsOf(text, find) {
    const starts = [];
    for(let at = text.indexOf(find); at !== -1; at = text.indexOf(find, at + find.length))starts.push(at);
    return starts;
}
function pointAt(text, index, prefer) {
    const ending = /\S+$/.exec(text.slice(0, index))?.[0] ?? "";
    const starting = /^\S+/.exec(text.slice(index))?.[0] ?? "";
    const candidates = [
        [
            ending,
            index - ending.length,
            "end"
        ],
        [
            starting,
            index,
            "start"
        ]
    ];
    if (prefer === "start") candidates.reverse();
    for(let index = 0; index < candidates.length; index++){
        const [find, start, at] = candidates[index];
        if (!find || wordQuery(find).length > MAX_QUERY_LENGTH) continue;
        const starts = startsOf(text, find);
        const nth = starts.indexOf(start);
        if (nth !== -1) return {
            find,
            nth,
            count: starts.length,
            at
        };
    }
    return null;
}
const isSpace = (char)=>char !== undefined && /\s/.test(char);
function withoutSharedSpacing(start, remove, insert) {
    let head = 0;
    while(head < remove.length && remove[head] === insert[head] && isSpace(remove[head]))head++;
    let tail = 0;
    const fromEnd = (text)=>text[text.length - 1 - tail];
    const fits = ()=>tail < remove.length - head && tail < insert.length - head;
    while(fits() && fromEnd(remove) === fromEnd(insert) && isSpace(fromEnd(remove)))tail++;
    return {
        start: start + head,
        remove: remove.slice(head, remove.length - tail),
        insert: insert.slice(head, insert.length - tail)
    };
}
const wordCount = (text)=>text.match(/[\p{L}\p{N}]+/gu)?.length ?? 0;
export function textSplices(before, after) {
    const parts = diffWordsWithSpace(before, after);
    const removed = parts.reduce((sum, part)=>sum + (part.removed ? wordCount(part.value) : 0), 0);
    if (removed > wordCount(before) * MAX_REMOVED_SHARE) return null;
    const hunks = [];
    let index = 0;
    let isOpen = false;
    const isChange = (part)=>!!part && (part.added || part.removed);
    parts.forEach((part, at)=>{
        const isSpacingInHunk = isOpen && /^\s+$/.test(part.value) && isChange(parts[at + 1]);
        if (!isChange(part) && !isSpacingInHunk) {
            index += part.value.length;
            isOpen = false;
            return;
        }
        if (!isOpen) hunks.push({
            start: index,
            remove: "",
            insert: ""
        });
        isOpen = true;
        const open = hunks[hunks.length - 1];
        if (!part.added) {
            open.remove += part.value;
            index += part.value.length;
        }
        if (!part.removed) open.insert += part.value;
    });
    const splices = hunks.map(({ start, remove, insert })=>withoutSharedSpacing(start, remove, insert)).filter(({ remove, insert })=>remove || insert).map(({ start, remove, insert })=>{
        const from = pointAt(before, start, "start");
        const to = pointAt(before, start + remove.length, "end");
        return from && to ? {
            from,
            to,
            remove,
            insert
        } : null;
    });
    return splices.every((splice)=>splice !== null) ? splices.reverse() : null;
}
